import { type Dayjs } from 'dayjs'
import { Block, DefaultBlockSchema } from '@packages/blocknote-core'
import { convertTo24HourFormat } from '../../utils/time'
import { calculateEventPosition } from './timeline/utils/event'
import {
  isBlockType,
  TASK_BLOCK_TYPES,
  isBlockCancelled,
  isBlockChecked,
} from '../../components/shared/BlockRenderer'

export type CalendarEvent = {
  id: string
  contentType: string
  content: string
  startDate: Dayjs
  endDate: Dayjs
  position: string
  checked?: boolean
  cancelled?: boolean
  startDateMatch?: string
  endDateMatch?: string
  fullDate?: string
  allDay?: boolean
  description?: string
  color?: string
  calendarName?: string
  calendarId?: string
}

type ProcessedResult = {
  startDateMatch: string
  endDateMatch: string
  fullDate: string
  startDate: Dayjs
  endDate: Dayjs
  position: string
}

// Block types that can contain calendar events
const EVENT_VALID_TYPES = [
  ...TASK_BLOCK_TYPES,
  'bulletListItem',
  'heading',
] as const

export function processEventsInBlock(
  block: Block<DefaultBlockSchema>,
  selectedDay: Dayjs
) {
  let events: CalendarEvent[] = []

  // Canceled tasks or checklists are not counted as timeblocks and shouldn't be displayed
  if (isBlockType(block, EVENT_VALID_TYPES) && !isBlockCancelled(block)) {
    let timeBlock: ProcessedResult | undefined
    let eventContent = ''

    for (const inlineContent of block.content) {
      if (inlineContent.type !== 'text') continue

      const match =
        inlineContent.styles.timeString &&
        /\d{1,2}\s*:\s*\d{2}\s*(?:AM|PM)?\s*-\s*\d{1,2}\s*:\s*\d{2}\s*(?:AM|PM)?/.exec(
          inlineContent.text
        )

      if (match) {
        const fullTime = match[0].split('-')
        const startTime: string = fullTime[0]
        const endTime: string = fullTime[1]
        const is24hours = !/AM|PM/.test(startTime)
        const start24Hour = is24hours
          ? startTime
          : convertTo24HourFormat(startTime)
        const end24Hour = is24hours
          ? endTime
          : convertTo24HourFormat(endTime.trim())
        // Validate time format before parsing
        const startParts = start24Hour.split(':')
        const endParts = end24Hour.split(':')

        if (startParts.length !== 2 || endParts.length !== 2) {
          console.warn('[CalendarEvent] Invalid time format:', {
            start24Hour,
            end24Hour,
          })
          continue
        }

        const startHour = Number.parseInt(startParts[0], 10)
        const startMinute = Number.parseInt(startParts[1], 10)
        const endHour = Number.parseInt(endParts[0], 10)
        const endMinute = Number.parseInt(endParts[1], 10)

        // Validate parsed time values
        if (
          Number.isNaN(startHour) ||
          Number.isNaN(startMinute) ||
          Number.isNaN(endHour) ||
          Number.isNaN(endMinute) ||
          startHour < 0 ||
          startHour > 23 ||
          startMinute < 0 ||
          startMinute > 59 ||
          endHour < 0 ||
          endHour > 23 ||
          endMinute < 0 ||
          endMinute > 59
        ) {
          console.warn('[CalendarEvent] Invalid time values:', {
            startHour,
            startMinute,
            endHour,
            endMinute,
          })
          continue
        }

        const startDateTime = selectedDay
          .set('hour', startHour)
          .set('minute', startMinute)
          .set('second', 0)
          .set('millisecond', 0)
        let endDateTime = selectedDay
          .set('hour', endHour)
          .set('minute', endMinute)
          .set('second', 0)
          .set('millisecond', 0)

        // Validate that dayjs objects are valid
        if (!startDateTime.isValid() || !endDateTime.isValid()) {
          console.warn('[CalendarEvent] Invalid dayjs objects created:', {
            startDateTime: startDateTime.toString(),
            endDateTime: endDateTime.toString(),
            selectedDay: selectedDay.toString(),
          })
          continue
        }

        if (startDateTime > endDateTime) {
          endDateTime = startDateTime.add(15, 'minute')
        }

        timeBlock = {
          startDateMatch: startTime,
          endDateMatch: endTime,
          fullDate: inlineContent.text,
          startDate: startDateTime,
          endDate: endDateTime,
          position: calculateEventPosition(
            startDateTime,
            endDateTime,
            selectedDay
          ),
        }
      } else {
        eventContent += `${inlineContent.text} `
      }
    }

    if (timeBlock && eventContent.trim().length > 0) {
      events.push({
        ...timeBlock,
        content: eventContent.trim(),
        id: block.id,
        contentType: block.type,
        checked: isBlockChecked(block),
        cancelled: isBlockCancelled(block),
      })
    }
  }

  for (const child of block.children) {
    events = [...events, ...processEventsInBlock(child, selectedDay)]
  }

  return events
}
